import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createAdUnit, updateAdUnit } from '../adActions';
import { apiClient } from '../../../shared/utils/api';
import { AD_SIZES, AD_FAMILIES, AD_STATUSES, APP_ROUTES } from '../../../shared/constants';
import { extractContractError, formatErrorForDisplay, useIdempotencyKey } from '@bafkal/ui-shared/utils';
import { validateAdUnitCreateRequest } from '../../../shared/utils/contractValidation';
import { FormField, TextInput, Textarea, Select } from '@bafkal/ui-shared/forms';
import { Card, ErrorMessage, Button } from '@bafkal/ui-shared/components';

// Returns the first size key whose family matches, or '' if none defined yet.
const firstSizeForFamily = (family) => {
    const entry = Object.entries(AD_SIZES).find(([, v]) => v.family === family);
    return entry ? entry[0] : '';
};

// Only show families that have at least one size defined in the semantic format map.
const SUPPORTED_FAMILIES = AD_FAMILIES.filter(
    family => Object.values(AD_SIZES).some(v => v.family === family)
);

const AdForm = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Idempotency key for safe create retries — tied to this form session,
    // not per-submit, so retrying a failed network call won't create duplicates.
    const idempotencyKey = useIdempotencyKey();

    const [adData, setAdData] = useState({
        name: '',
        description: '',
        size: firstSizeForFamily(SUPPORTED_FAMILIES[0]),
        type: SUPPORTED_FAMILIES[0],
        status: AD_STATUSES[0],
        floorPrice: ''
    });

    const isMounted = useRef(true);

    useEffect(() => {
        const loadAd = async () => {
            if (id) {
                try {
                    // If editing, fetch the ad data via shared apiClient
                    const resp = await apiClient.get(`/inventory/adunit/${id}`);
                    if (isMounted.current) {
                        // Merge with default values to ensure all fields are defined
                        setAdData(prev => ({
                            ...prev,
                            ...resp.data,
                            name: resp.data.name || '',
                            description: resp.data.description || '',
                            size: resp.data.size || firstSizeForFamily(resp.data.type || AD_FAMILIES[0]),
                            type: resp.data.type || AD_FAMILIES[0],
                            status: resp.data.status || AD_STATUSES[0],
                            floorPrice: resp.data.floorPrice || ''
                        }));
                    }
                } catch (err) {
                    console.error('Failed to load ad data', err);
                    if (isMounted.current) {
                        const contractError = extractContractError(err, 'Failed to load ad data');
                        setError(contractError.message);
                    }
                }
            }
        };

        loadAd();

        return () => {
            isMounted.current = false;
        };
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'type') {
            // Reset size to first matching size for the new family
            setAdData(prev => ({
                ...prev,
                type: value,
                size: firstSizeForFamily(value),
            }));
        } else {
            setAdData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Coerce description to null when empty — contract defines it as string | null
        const payload = {
            ...adData,
            description: adData.description || null,
        };

        // Validate against AdUnitCreateRequest contract schema before sending
        const { valid, errors: validationErrors } = validateAdUnitCreateRequest(payload);
        if (!valid) {
            setError(validationErrors.join(' '));
            return;
        }

        if (isMounted.current) {
            setLoading(true);
            setError(null);
        }

        try {
            if (id) {
                await dispatch(updateAdUnit(id, payload));
            } else {
                await dispatch(createAdUnit(payload, idempotencyKey));
            }
            if (isMounted.current) history.push(APP_ROUTES.INVENTORY.ROOT);
        } catch (err) {
            const contractError = extractContractError(err, 'Failed to save ad unit');
            if (isMounted.current) setError(formatErrorForDisplay(contractError));
        } finally {
            if (isMounted.current) setLoading(false);
        }
    };

    return (
        <div className="py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <Card>
                    <h1 className="text-2xl font-semibold text-gray-900 mb-8">
                        {id ? 'Edit Ad Unit' : 'Create New Ad Unit'}
                    </h1>

                    <ErrorMessage message={error} />

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        <FormField label="Ad Name">
                            <TextInput
                                name="name"
                                id="name"
                                required
                                value={adData.name || ''}
                                onChange={handleChange}
                                placeholder="Enter a name for your ad"
                            />
                        </FormField>

                        <FormField label="Description">
                            <Textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={adData.description || ''}
                                onChange={handleChange}
                                placeholder="Describe your ad"
                            />
                        </FormField>

                        <FormField label="Ad Type">
                            <Select
                                id="type"
                                name="type"
                                required
                                value={adData.type || SUPPORTED_FAMILIES[0]}
                                onChange={handleChange}
                            >
                                {SUPPORTED_FAMILIES.map(family => (
                                    <option key={family} value={family}>
                                        {family.charAt(0).toUpperCase() + family.slice(1)}
                                    </option>
                                ))}
                            </Select>
                        </FormField>

                        <FormField label="Ad Size">
                            <Select
                                id="size"
                                name="size"
                                value={adData.size || ''}
                                onChange={handleChange}
                                disabled={!adData.size}
                            >
                                {Object.entries(AD_SIZES)
                                    .filter(([, v]) => v.family === adData.type)
                                    .map(([key, { label, dimensions }]) => (
                                        <option key={key} value={key}>
                                            {dimensions
                                                ? `${label} (${dimensions.width}x${dimensions.height})`
                                                : label}
                                        </option>
                                    ))
                                }
                                {!Object.values(AD_SIZES).some(v => v.family === adData.type) && (
                                    <option value="" disabled>No sizes defined for this type</option>
                                )}
                            </Select>
                        </FormField>

                        <FormField label="Floor Price (USD)" helperText="Minimum bid price in US dollars.">
                            <TextInput
                                type="number"
                                name="floorPrice"
                                id="floorPrice"
                                step="0.01"
                                min="0"
                                required
                                value={adData.floorPrice || ''}
                                onChange={handleChange}
                                placeholder="0.00"
                            />
                        </FormField>

                        <FormField label="Status">
                            <Select
                                id="status"
                                name="status"
                                value={adData.status || AD_STATUSES[0]}
                                onChange={handleChange}
                            >
                                {AD_STATUSES.map(s => (
                                    <option key={s} value={s}>
                                        {s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()}
                                    </option>
                                ))}
                            </Select>
                        </FormField>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px' }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => history.push(APP_ROUTES.INVENTORY.ROOT)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Saving…' : 'Save Ad Unit'}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default AdForm;